/* =============================================================
   Boxershorts PDP — client-side interactivity
   - Gallery thumbnail swap
   - Option pickers (Farbe / Größe / Set)
   - Variant lookup + price / stock / CTA sync
   - Accordion deep-jump ("Größentabelle" → open + scroll)
   ============================================================= */
(function () {
  'use strict';

  const roots = document.querySelectorAll('[data-pdp-bx-root]');
  if (!roots.length) return;

  roots.forEach(initRoot);

  function initRoot(root) {
    // ----- Variant data
    let variants = [];
    try {
      const jsonEl = root.querySelector('[data-pdp-bx-variants]');
      if (jsonEl) variants = JSON.parse(jsonEl.textContent);
    } catch (e) { /* noop */ }

    // ----- Selected values (from initial aria-pressed=true buttons)
    const selected = {}; // { position: value }
    root.querySelectorAll('[data-pdp-bx-option][aria-pressed="true"]').forEach(btn => {
      const pos = btn.getAttribute('data-pdp-bx-option');
      const val = btn.getAttribute('data-pdp-bx-value');
      selected[pos] = val;
    });

    // ----- Option pickers
    root.querySelectorAll('[data-pdp-bx-option]').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const pos = this.getAttribute('data-pdp-bx-option');
        const val = this.getAttribute('data-pdp-bx-value');
        selected[pos] = val;

        // toggle pressed state within same option group
        root.querySelectorAll('[data-pdp-bx-option="' + pos + '"]').forEach(sib => {
          const on = sib === this;
          sib.setAttribute('aria-pressed', on ? 'true' : 'false');
          sib.setAttribute('aria-checked', on ? 'true' : 'false');
        });

        syncLabels(root, pos, val);
        applyVariant(root, variants, selected);
        // Farbe geaendert → Thumbs filtern und Hauptbild auf ersten sichtbaren setzen
        if (Number(pos) === colorPosition) {
          filterThumbsByColor(val);
          activateThumb(0);
        }
      });
    });

    // ----- Gallery: thumbnails + prev/next arrows
    const mainImg = root.querySelector('[data-pdp-bx-main-image]');
    const thumbs = Array.from(root.querySelectorAll('[data-pdp-bx-thumb]'));
    const thumbStrip = root.querySelector('[data-pdp-bx-thumbs]');
    let galleryIndex = 0;

    // Media → Colors Mapping (welche Farben referenzieren welches Bild)
    let mediaColors = {};
    try {
      const mcEl = root.querySelector('[data-pdp-bx-media-colors]');
      if (mcEl) mediaColors = JSON.parse(mcEl.textContent);
    } catch (e) { /* noop */ }

    // Aktuell sichtbare Thumbs (gefiltert nach Farbe). Default: alle.
    let visibleThumbs = thumbs.slice();

    // Position des Color-Options: bevorzugt der Container mit pdp-bx-swatches (Farb-Kacheln),
    // sonst Fallback auf den ersten Option-Button in einem .pdp-bx-swatches Container.
    let colorPosition = 0;
    const swatchBtn = root.querySelector('.pdp-bx-swatches [data-pdp-bx-option]');
    if (swatchBtn) {
      colorPosition = Number(swatchBtn.getAttribute('data-pdp-bx-option')) || 0;
    }
    if (!colorPosition) {
      // Notfall: erste Option
      const optionPositions = Object.keys(selected).map(Number).sort((a, b) => a - b);
      colorPosition = optionPositions[0] || 1;
    }

    // Liste aller Farben-Werte (fuer URL-Fallback und "shared"-Detection)
    let colorValues = [];
    try {
      const cvEl = root.querySelector('[data-pdp-bx-color-values]');
      if (cvEl) colorValues = JSON.parse(cvEl.textContent);
    } catch (e) { /* noop */ }

    function normalizeStr(s) {
      // ae/oe/ue/ss + Diakritika normalisieren fuer Vergleich
      return (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/ß/g, 'ss')
        .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u');
    }

    function detectColorsFromThumb(thumb) {
      const img = thumb.querySelector('img');
      const src = img ? (img.getAttribute('src') || '') : '';
      const full = thumb.getAttribute('data-full') || '';
      const alt = img ? (img.getAttribute('alt') || '') : '';
      const hay = normalizeStr(src + ' ' + full + ' ' + alt);
      const found = [];
      colorValues.forEach(cv => {
        // ganze Farbe: "schwarz", "weiss", "mix (schwarz/grau/weiss)", "hellgrau"
        // fuer "Mix ..."-Werte nur den ersten Token nehmen
        const normalized = normalizeStr(cv);
        const firstToken = normalized.split(/[\s(_\-\/]/)[0];
        if (firstToken && hay.indexOf(firstToken) !== -1) found.push(cv);
      });
      return found;
    }

    function filterThumbsByColor(colorName) {
      if (!thumbs.length) return;
      const hasMapping = mediaColors && Object.keys(mediaColors).length > 0;
      const targetNorm = normalizeStr(colorName);
      const targetFirstToken = targetNorm.split(/[\s(_\-\/]/)[0];

      // Fuer jeden Thumb effektive Farb-Liste bestimmen (Server-Mapping ODER URL/Alt-Fallback)
      const effective = new Map();
      thumbs.forEach(t => {
        const mid = t.getAttribute('data-media-id');
        let cs = hasMapping ? mediaColors[mid] : null;
        if (!cs || cs.length === 0) cs = detectColorsFromThumb(t);
        effective.set(t, cs || []);
      });

      // Existieren farb-spezifische Bilder fuer die aktuelle Farbe ueberhaupt?
      let anyColorSpecificExists = false;
      for (const arr of effective.values()) {
        if (arr.some(c => normalizeStr(c) === targetNorm || normalizeStr(c).split(/[\s(_\-\/]/)[0] === targetFirstToken)) {
          anyColorSpecificExists = true;
          break;
        }
      }

      visibleThumbs = thumbs.filter(t => {
        if (!colorName) return true;
        const cs = effective.get(t) || [];
        if (cs.length === 0) return !anyColorSpecificExists; // "shared" nur wenn nix Farb-spezifisches
        return cs.some(c => {
          const cn = normalizeStr(c);
          return cn === targetNorm || cn.split(/[\s(_\-\/]/)[0] === targetFirstToken;
        });
      });

      // Fallback: leer nach Filter → alles zeigen statt Blackout
      if (visibleThumbs.length === 0) visibleThumbs = thumbs.slice();

      thumbs.forEach(t => {
        const on = visibleThumbs.indexOf(t) !== -1;
        t.hidden = !on;
        t.style.display = on ? '' : 'none';
      });
    }

    function activateThumb(idx) {
      if (!visibleThumbs.length) return;
      const list = visibleThumbs;
      const bounded = ((idx % list.length) + list.length) % list.length;
      galleryIndex = bounded;
      const target = list[bounded];
      const full = target.getAttribute('data-full');
      if (mainImg && full) {
        // srcset entfernen — sonst gewinnt srcset über src und das Hauptbild bleibt beim Ursprung
        mainImg.removeAttribute('srcset');
        mainImg.removeAttribute('sizes');
        mainImg.src = full;
      }
      thumbs.forEach(t => {
        const on = t === target;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      // scroll thumbnail strip so active one is visible
      if (thumbStrip && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
      // sync feature-bar: nutze Position innerhalb der aktuell sichtbaren Liste
      // (Bild 1 einer Farbe = keine Bar, Bild 2 = byIndex[1], …)
      updateFeatureBar(bounded);
    }

    // ----- Feature-Bar (unter Hauptbild, pro Slide)
    const featureBar = root.querySelector('[data-pdp-bx-feature-bar]');
    const featureText = root.querySelector('[data-pdp-bx-feature-text]');
    const featureTrigger = root.querySelector('[data-pdp-bx-feature-trigger]');
    let featureData = { default: { text: '', details: '' }, byIndex: {} };
    try {
      const dataEl = root.querySelector('[data-pdp-bx-feature-data]');
      if (dataEl) featureData = JSON.parse(dataEl.textContent);
    } catch (e) { /* noop */ }

    function updateFeatureBar(idx) {
      if (!featureBar) return;

      // Bild 1 (Hauptbild) bekommt NIE eine Bar — wie bei Snocks
      if (idx === 0) {
        featureBar.hidden = true;
        return;
      }

      const slot = featureData.byIndex && featureData.byIndex[idx];
      const text = (slot && slot.text) || featureData.default.text || '';
      const details = (slot && slot.details) || featureData.default.details || '';

      if (text) {
        if (featureText) featureText.textContent = text;
        featureBar.hidden = false;
        if (featureTrigger) {
          if (details) {
            featureTrigger.hidden = false;
            featureTrigger._details = details;
            featureTrigger._title = text;
          } else {
            featureTrigger.hidden = true;
          }
        }
      } else {
        featureBar.hidden = true;
      }
    }

    // ----- Modal für Feature-Bar Details
    const modal = root.querySelector('[data-pdp-bx-modal]');
    const modalTitle = root.querySelector('[data-pdp-bx-modal-title]');
    const modalBody = root.querySelector('[data-pdp-bx-modal-body]');

    function openModal(title, bodyHtml) {
      if (!modal) return;
      if (modalTitle) modalTitle.textContent = title || '';
      if (modalBody) modalBody.innerHTML = bodyHtml || '';
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closeModal() {
      if (!modal) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    if (featureTrigger) {
      // Tooltip-Element (klein, positioniert über dem "i") vs. Modal (groß, für Klick)
      let tooltipEl = root.querySelector('[data-pdp-bx-feature-tooltip]');
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'pdp-bx-feature-tooltip';
        tooltipEl.setAttribute('data-pdp-bx-feature-tooltip', '');
        tooltipEl.hidden = true;
        // an die Feature-Bar hängen (Position relative)
        (featureBar || document.body).appendChild(tooltipEl);
      }

      const iconEl = featureTrigger.querySelector('.pdp-bx-feature-bar__icon');
      const linkEl = featureTrigger.querySelector('.pdp-bx-feature-bar__link');

      const showTooltip = function () {
        const details = featureTrigger._details || '';
        if (!details) return;
        tooltipEl.innerHTML = details;
        tooltipEl.hidden = false;
      };
      const hideTooltip = function () { tooltipEl.hidden = true; };
      // Klick auf den ganzen Trigger nicht mehr Modal öffnen
      const swallow = function (e) { e.preventDefault(); e.stopPropagation(); };

      [iconEl, linkEl].forEach(el => {
        if (!el) return;
        el.addEventListener('mouseenter', showTooltip);
        el.addEventListener('mouseleave', hideTooltip);
        el.addEventListener('click', swallow);
      });
    }

    // ----- Size-Chart Modal Trigger (Klick auf "Größentabelle"-Link neben Größe)
    const sizeChartContent = root.querySelector('[data-pdp-bx-size-chart-content]');
    const sizeChartHtml = sizeChartContent ? sizeChartContent.innerHTML : '';
    root.querySelectorAll('[data-pdp-bx-size-chart-open]').forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (sizeChartHtml) openModal('Größentabelle', sizeChartHtml);
      });
    });

    if (modal) {
      root.querySelectorAll('[data-pdp-bx-modal-close]').forEach(el => {
        el.addEventListener('click', closeModal);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) closeModal();
      });
    }

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', function () {
        // Klick-Ziel-Index bezogen auf die AKTUELL sichtbare Liste
        const vi = visibleThumbs.indexOf(this);
        if (vi >= 0) activateThumb(vi);
      });
    });

    root.querySelectorAll('[data-pdp-bx-nav]').forEach(btn => {
      btn.addEventListener('click', function () {
        const dir = this.getAttribute('data-pdp-bx-nav');
        activateThumb(galleryIndex + (dir === 'next' ? 1 : -1));
      });
    });

    // Keyboard: arrow keys on main image
    const galleryMain = root.querySelector('.pdp-bx-gallery__main');
    if (galleryMain) {
      galleryMain.setAttribute('tabindex', '0');
      galleryMain.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); activateThumb(galleryIndex - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); activateThumb(galleryIndex + 1); }
      });
    }

    // ----- Accordion jump
    root.querySelectorAll('[data-pdp-bx-acc-jump]').forEach(btn => {
      btn.addEventListener('click', function () {
        const key = this.getAttribute('data-pdp-bx-acc-jump');
        const target = root.querySelector('[data-pdp-bx-acc-target="' + key + '"]');
        if (target) {
          target.open = true;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });

    // ----- AJAX Add-to-Cart mit Cart-Notification-Drawer öffnen
    root.querySelectorAll('form[data-pdp-bx-ajax-form]').forEach(form => {
      form.addEventListener('submit', function(e) {
        // Wenn Theme's <product-form> das Element schon uebernommen hat, nicht doppelt handeln
        if (form.closest('product-form')?.dataset?.pdpBxHandled) return;

        e.preventDefault();
        e.stopPropagation();
        form.closest('product-form')?.setAttribute?.('data-pdp-bx-handled', '1');

        const submitBtn = form.querySelector('[data-pdp-bx-submit]');
        const ctaText = form.querySelector('[data-pdp-bx-cta-text]');
        const originalText = ctaText ? ctaText.textContent : '';
        if (submitBtn) submitBtn.setAttribute('disabled', '');
        if (ctaText) ctaText.textContent = 'Wird hinzugefuegt…';

        const formData = new FormData(form);
        // sections-Parameter fuer serverseitiges Rendering der Cart-Drawer (Impact)
        formData.append('sections', 'cart-notification-drawer,cart-drawer,cart-icon-bubble,header');

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        })
        .then(r => r.ok ? r.json() : r.json().then(err => { throw err; }))
        .then(data => {
          // Alle bekannten Cart-Refresh-Events streuen
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: data }));
          document.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: data }));

          // Cart-Notification-Drawer oeffnen (Impact-Theme)
          const drawer = document.querySelector('cart-notification-drawer, cart-drawer, cart-notification');
          if (drawer) {
            if (typeof drawer.show === 'function') drawer.show();
            else if (typeof drawer.open === 'function') drawer.open();
            else drawer.setAttribute?.('open', '');
          } else {
            // Fallback: Cart-Icon klicken oder zum Warenkorb
            const cartOpener = document.querySelector('[aria-controls*="cart"], a[href="/cart"], .header-actions__cart, [data-cart-drawer-open]');
            if (cartOpener && typeof cartOpener.click === 'function') cartOpener.click();
          }
        })
        .catch(err => {
          console.warn('[pdp-bx] Add-to-cart error:', err);
          window.location.href = '/cart';
        })
        .finally(() => {
          if (submitBtn) submitBtn.removeAttribute('disabled');
          if (ctaText) ctaText.textContent = originalText;
          form.closest('product-form')?.removeAttribute?.('data-pdp-bx-handled');
        });
      });
    });

    // Initial sync
    applyVariant(root, variants, selected);
    // Initial Farb-Filter
    if (selected[colorPosition]) {
      filterThumbsByColor(selected[colorPosition]);
      activateThumb(0);
    }
    updateFeatureBar(0);
  }

  function syncLabels(root, pos, val) {
    // update the "Farbe: <b>Value</b>" / "Größe: <b>Value</b>" mirrors near the group heading
    const group = root.querySelector('[data-pdp-bx-option="' + pos + '"]');
    if (!group) return;
    const optgroup = group.closest('.pdp-bx-optgroup');
    if (!optgroup) return;
    const labelEl = optgroup.querySelector('[data-pdp-bx-color-label], [data-pdp-bx-size-label], .pdp-bx-optgroup__value');
    if (labelEl) labelEl.textContent = val;
  }

  function applyVariant(root, variants, selected) {
    if (!variants || !variants.length) return;

    // Build an ordered options array from position keys
    const positions = Object.keys(selected).map(Number).sort((a, b) => a - b);
    if (!positions.length) return;

    const match = variants.find(v => {
      return positions.every(pos => {
        const idx = pos - 1;
        return v.options[idx] === selected[pos];
      });
    });

    // Availability: if a size button has no matching available variant for the current color, disable it
    updateOptionAvailability(root, variants, selected);

    if (!match) {
      setUnavailable(root);
      return;
    }

    // ----- Variant ID
    const vidInput = root.querySelector('[data-pdp-bx-variant-id]');
    if (vidInput) vidInput.value = match.id;

    // ----- Price
    const priceEl = root.querySelector('[data-pdp-bx-price]');
    if (priceEl) priceEl.textContent = formatMoney(match.price);

    const priceMirror = root.querySelector('[data-pdp-bx-price-mirror]');
    if (priceMirror) priceMirror.textContent = formatMoney(match.price);

    const perUnitEl = root.querySelector('[data-pdp-bx-per-unit]');
    if (perUnitEl) {
      const packSize = parseInt(perUnitEl.getAttribute('data-pack-size') || '3', 10);
      const factor = packSize > 0 ? packSize : 3;
      perUnitEl.textContent = 'Nur ' + formatMoney(Math.round(match.price / factor)) + ' / Stück';
    }

    // ----- Stock
    const stockEl = root.querySelector('[data-pdp-bx-stock]');
    const stockText = root.querySelector('[data-pdp-bx-stock-text]');
    const cta = root.querySelector('[data-pdp-bx-submit]');
    const ctaText = root.querySelector('[data-pdp-bx-cta-text]');

    if (match.available) {
      if (stockEl) stockEl.classList.remove('pdp-bx-stock--out');
      if (stockText) stockText.textContent = 'Auf Lager — in 1–3 Werktagen bei dir!';
      if (cta) {
        cta.removeAttribute('disabled');
        cta.removeAttribute('aria-disabled');
      }
      if (ctaText && cta) ctaText.textContent = cta.getAttribute('data-cta-text') || 'In den Warenkorb legen';
    } else {
      if (stockEl) stockEl.classList.add('pdp-bx-stock--out');
      if (stockText) stockText.textContent = 'Momentan ausverkauft';
      if (cta) {
        cta.setAttribute('disabled', '');
        cta.setAttribute('aria-disabled', 'true');
      }
      if (ctaText && cta) ctaText.textContent = cta.getAttribute('data-cta-unavailable') || 'Momentan ausverkauft';
    }

    // Featured-Media Swap wird jetzt von filterThumbsByColor + activateThumb im initRoot-Scope gemacht
    // (nur bei Color-Wechsel — Size-Wechsel soll NICHT das Bild zurueckspringen)
  }

  function setUnavailable(root) {
    const cta = root.querySelector('[data-pdp-bx-submit]');
    const ctaText = root.querySelector('[data-pdp-bx-cta-text]');
    const stockEl = root.querySelector('[data-pdp-bx-stock]');
    const stockText = root.querySelector('[data-pdp-bx-stock-text]');
    if (cta) { cta.setAttribute('disabled', ''); cta.setAttribute('aria-disabled', 'true'); }
    if (ctaText) ctaText.textContent = 'Kombination nicht verfügbar';
    if (stockEl) stockEl.classList.add('pdp-bx-stock--out');
    if (stockText) stockText.textContent = 'Bitte andere Kombination wählen';
  }

  function updateOptionAvailability(root, variants, selected) {
    // For every option group, disable values that have no available variant given the OTHER selected options.
    const groups = {};
    root.querySelectorAll('[data-pdp-bx-option]').forEach(btn => {
      const pos = btn.getAttribute('data-pdp-bx-option');
      (groups[pos] = groups[pos] || []).push(btn);
    });

    Object.keys(groups).forEach(pos => {
      const posN = Number(pos);
      groups[pos].forEach(btn => {
        const val = btn.getAttribute('data-pdp-bx-value');
        const testSelection = Object.assign({}, selected, { [pos]: val });
        const positions = Object.keys(testSelection).map(Number).sort((a, b) => a - b);
        const match = variants.find(v =>
          positions.every(p => v.options[p - 1] === testSelection[p])
        );
        const isDisabled = !match || !match.available;
        if (isDisabled) {
          btn.setAttribute('disabled', '');
        } else {
          btn.removeAttribute('disabled');
        }
      });
    });
  }

  function formatMoney(cents) {
    // Shopify prices are in the store's smallest currency unit (cents for EUR)
    const val = (cents / 100).toFixed(2).replace('.', ',');
    return val + ' €';
  }
})();
